import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { SlidersHorizontal, Save, RotateCcw, Banknote, Ticket } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "total_revenue", label: "Total Revenue", prefix: "$", suffix: "", min: 0 },
  { key: "net_profit", label: "Net Profit", prefix: "$", suffix: "" },
  { key: "margin", label: "Net Margin", prefix: "", suffix: "%", min: 0, max: 100 },
  { key: "health_score", label: "AI Health Score", prefix: "", suffix: "/100", min: 0, max: 100 },
];

export default function FinancialControls({ onSaved }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/financials");
    setData(data);
    const f = {};
    FIELDS.forEach(({ key }) => { f[key] = data.overrides[key] != null ? String(data.overrides[key]) : ""; });
    setForm(f);
  };
  useEffect(() => { load(); }, []);

  const persist = async (payload, msg) => {
    setSaving(true);
    try {
      await api.put("/admin/financials", payload);
      toast.success(msg);
      await load();
      onSaved && onSaved();
    } catch { toast.error("Could not save overrides"); }
    finally { setSaving(false); }
  };

  const save = () => {
    const payload = {};
    FIELDS.forEach(({ key }) => { payload[key] = form[key] === "" ? null : Number(form[key]); });
    persist(payload, "Financial overrides applied");
  };

  const reset = () => persist({ total_revenue: null, net_profit: null, margin: null, health_score: null }, "Reverted to AI/system values");

  const hasOverride = data && FIELDS.some(({ key }) => data.overrides[key] != null);
  const rr = data?.real_revenue;

  return (
    <div className="mt-8" data-testid="financial-controls">
      <h3 className="font-display font-bold text-2xl uppercase mb-1 flex items-center gap-2">
        <SlidersHorizontal className="w-6 h-6 text-pb-red" /> Financial Controls
      </h3>
      <p className="text-sm text-zinc-500 mb-5">Manually override key financials. Any value you set <span className="text-pb-cyan">supersedes</span> the AI/system-computed number across the dashboard. Leave a box empty to keep the automatic value.</p>

      {rr && (
        <div className="bg-pb-surface border border-pb-cyan/40 p-5 mb-5" data-testid="real-revenue-panel">
          <div className="flex items-center gap-2 mb-3">
            <Banknote className="w-5 h-5 text-pb-cyan" />
            <span className="font-display font-bold text-lg uppercase">Real Stripe Revenue</span>
            <span className="text-[10px] uppercase tracking-widest text-pb-cyan bg-pb-cyan/10 border border-pb-cyan/30 px-2 py-0.5">Live</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[["Memberships", rr.memberships, "rev-memberships"], ["League Entries", rr.leagues, "rev-leagues"],
              ["Store Orders", rr.store, "rev-store"], ["Session Passes", rr.sessions, "rev-passes"],
              ["Total", rr.total, "rev-total"]].map(([l, v, tid]) => (
              <div key={l} className={`border p-3 ${l === "Total" ? "border-pb-red bg-pb-red/5" : "border-pb-border bg-pb-elevated"}`}>
                <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{l}</div>
                <div data-testid={tid} className={`font-display font-black text-lg ${l === "Total" ? "text-pb-red" : "text-pb-cyan"}`}>${Number(v || 0).toFixed(2)}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">Computed from actual paid Stripe transactions — this feeds the auto Total Revenue below.</p>
        </div>
      )}

      <div className="bg-pb-surface border border-pb-border p-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FIELDS.map(({ key, label, prefix, suffix, min, max }) => {
            const computed = data?.computed?.[key];
            const overridden = data?.overrides?.[key] != null;
            return (
              <div key={key}>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</label>
                  {overridden && <span className="text-[9px] uppercase tracking-widest text-pb-red font-bold">Manual</span>}
                </div>
                <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-pb-red">
                  {prefix && <span className="pl-3 text-zinc-500 text-sm">{prefix}</span>}
                  <input data-testid={`fin-${key}`} type="number" value={form[key] ?? ""} min={min} max={max}
                    placeholder={computed != null ? String(computed) : "0"}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full bg-transparent px-2 py-2.5 text-white placeholder-zinc-600 focus:outline-none" />
                  {suffix && <span className="pr-3 text-zinc-500 text-sm">{suffix}</span>}
                </div>
                <div className="text-[10px] text-zinc-600 mt-1.5 font-mono">AI/System: {prefix}{computed ?? 0}{suffix}</div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 mt-5">
          <button data-testid="save-financials-btn" onClick={save} disabled={saving}
            className="flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Apply Overrides"}
          </button>
          <button data-testid="reset-financials-btn" onClick={reset} disabled={saving || !hasOverride}
            className="flex items-center gap-2 border border-pb-border text-zinc-300 font-bold uppercase tracking-widest px-5 py-2.5 hover:border-pb-red transition-colors disabled:opacity-40">
            <RotateCcw className="w-4 h-4" /> Revert to Auto
          </button>
          {data?.updated_at && (
            <span className="text-xs text-zinc-600 font-mono">Last edited {String(data.updated_at).slice(0, 10)}{data.updated_by ? ` · ${data.updated_by}` : ""}</span>
          )}
        </div>
      </div>

      <PassPricing />
    </div>
  );
}

function PassPricing() {
  const [passes, setPasses] = useState([]);
  const [form, setForm] = useState({});
  const [names, setNames] = useState({});
  const [descs, setDescs] = useState({});
  const [coins, setCoins] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/pass-prices");
    setPasses(data.passes);
    const f = {}, n = {}, d = {}, c = {};
    data.passes.forEach((p) => { f[p.id] = String(p.price); n[p.id] = p.name; d[p.id] = p.desc; c[p.id] = p.price_coins ? String(p.price_coins) : ""; });
    setForm(f);
    setNames(n);
    setDescs(d);
    setCoins(c);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pass-prices", {
        dropin: Number(form.dropin), weekly: Number(form.weekly), monthly: Number(form.monthly),
        dropin_name: names.dropin?.trim(), weekly_name: names.weekly?.trim(), monthly_name: names.monthly?.trim(),
        dropin_desc: descs.dropin?.trim(), weekly_desc: descs.weekly?.trim(), monthly_desc: descs.monthly?.trim(),
        dropin_coins: Number(coins.dropin) || 0, weekly_coins: Number(coins.weekly) || 0, monthly_coins: Number(coins.monthly) || 0,
      });
      toast.success("Pass names, prices & descriptions updated — live in the Book tab & cart");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save passes"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-pb-surface border border-pb-border p-5 mt-5" data-testid="pass-pricing">
      <div className="flex items-center gap-2 mb-1">
        <Ticket className="w-5 h-5 text-pb-red" />
        <span className="font-display font-bold text-lg uppercase">Session Pass Pricing</span>
      </div>
      <p className="text-sm text-zinc-500 mb-4">Rename passes and change what members pay. Updates apply instantly to the Book tab, the cart, and Stripe checkout.</p>
      <div className="grid sm:grid-cols-3 gap-4">
        {passes.map((p) => (
          <div key={p.id}>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Pass Name</label>
              {(Number(form[p.id]) !== p.default_price || names[p.id] !== p.default_name) && <span className="text-[9px] uppercase tracking-widest text-pb-red font-bold">Custom</span>}
            </div>
            <input data-testid={`pass-name-${p.id}`} type="text" maxLength={60} value={names[p.id] ?? ""}
              onChange={(e) => setNames({ ...names, [p.id]: e.target.value })}
              className="w-full bg-pb-elevated border border-pb-border focus:border-pb-red px-3 py-2.5 text-white text-sm font-semibold focus:outline-none mb-2" />
            <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-pb-red">
              <span className="pl-3 text-zinc-500 text-sm">$</span>
              <input data-testid={`pass-price-${p.id}`} type="number" min={1} value={form[p.id] ?? ""}
                onChange={(e) => setForm({ ...form, [p.id]: e.target.value })}
                className="w-full bg-transparent px-2 py-2.5 text-white focus:outline-none" />
            </div>
            <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-purple-400 mt-2">
              <span className="pl-3 text-purple-300 text-sm">🪙</span>
              <input data-testid={`pass-coins-${p.id}`} type="number" min={0} value={coins[p.id] ?? ""}
                placeholder="Coin price (blank = no coin pay)"
                onChange={(e) => setCoins({ ...coins, [p.id]: e.target.value })}
                className="w-full bg-transparent px-2 py-2.5 text-white text-sm placeholder-zinc-600 focus:outline-none" />
            </div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2 mb-1.5">Description</label>
            <textarea data-testid={`pass-desc-${p.id}`} rows={3} maxLength={1000} value={descs[p.id] ?? ""}
              onChange={(e) => setDescs({ ...descs, [p.id]: e.target.value })}
              placeholder="What members get with this pass…"
              className="w-full bg-pb-elevated border border-pb-border focus:border-pb-red px-3 py-2 text-white text-xs focus:outline-none resize-y" />
            <div className="text-[10px] text-zinc-600 mt-1.5 font-mono">Default: {p.default_name} · ${p.default_price}</div>
          </div>
        ))}
      </div>
      <button data-testid="save-pass-prices-btn" onClick={save} disabled={saving}
        className="mt-4 flex items-center gap-2 bg-pb-red text-black font-bold uppercase tracking-widest px-5 py-2.5 hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
        <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Passes"}
      </button>
    </div>
  );
}
